import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import Button from '../../components/Button';
import { ArrowLeft, Save } from 'lucide-react';
import apiService from '../../services/apiService';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext'; // Import useAuth

interface Client {
  _id: string;
  firstName: string;
  lastName: string;
  company?: string;
}

interface Invoice {
  _id: string;
  invoiceNumber: string;
  totalAmount?: number; // côté front
  clientId?: Client;    // selon API, parfois "client"
  client?: Client;
}

const AddPayment: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth(); // Use the auth hook

  const [formData, setFormData] = useState({
    paymentNumber: '',
    amount: 0,
    currency: 'EUR',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: '',
    status: 'completed',          // on encaisse => completed par défaut
    invoiceId: '',
    notes: '',
    transactionId: ''             // optionnel si tu l’utilises
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [invoices, setInvoices] = useState<Invoice[]>([]);

  const toNumber = (v: any, def = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : def;
  };

  const cleanPayload = (src: typeof formData) => {
    const out: any = {
      ...src,
      amount: toNumber(src.amount, 0),
    };
    // supprime les strings vides
    Object.keys(out).forEach(k => {
      if (out[k] === '') delete out[k];
    });
    return out;
  };

  // Préselection depuis /admin/payments/add?invoiceId=...&amount=...
  const preselected = useMemo(() => {
    const inv = searchParams.get('invoiceId') || '';
    const amt = searchParams.get('amount');
    return {
      invoiceId: inv,
      amount: amt ? toNumber(amt, 0) : undefined,
    };
  }, [searchParams]);

  useEffect(() => {
    if (user) { // Only fetch if user is available
      const fetchData = async () => {
        try {
          const params: { limit: number; companyId?: string } = { limit: 100 };

          // If user is admin and has a companyId, filter invoices by that companyId
          if (user && user.role === 'admin' && user.companyId) {
            params.companyId = user.companyId;
          }

          // On tente de supporter différentes formes de réponses API
          console.log('Fetching invoices with params:', params);
          const resp: any = await apiService.invoices.getAll?.(params) ?? {};
          // ⬇️ Correction: respecter la précédence avec des parenthèses
          const items =
            resp?.items ??
            (Array.isArray(resp) ? resp : []);

          setInvoices(Array.isArray(items) ? items : []);
          console.log('Invoices loaded for dropdown:', items);

          // Appliquer préselection si fournie
          setFormData(prev => ({
            ...prev,
            invoiceId: preselected.invoiceId || prev.invoiceId,
            amount: preselected.amount !== undefined ? preselected.amount : prev.amount,
          }));
        } catch (err) {
          console.error('Failed to fetch invoices:', err);
          setError('Impossible de charger la liste des factures.');
        } finally {
          setIsLoading(false);
        }
      };
      fetchData();
    }
  }, [user]); // Depend only on user

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'amount') {
      setFormData({ ...formData, [name]: toNumber(value, 0) });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleInvoiceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const invoiceId = e.target.value;
    setFormData(prev => ({ ...prev, invoiceId }));
    const inv = invoices.find(i => i._id === invoiceId);
    if (inv?.totalAmount && !preselected.amount) {
      // si pas déjà forcé par querystring, on préremplit le montant
      setFormData(prev => ({ ...prev, amount: toNumber(inv.totalAmount, prev.amount) }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    try {
      const payload = cleanPayload(formData);
      // L’API attend: { invoiceId, amount, paymentMethod, status, paymentDate, currency, paymentNumber, transactionId? }
      await apiService.payments.create(payload);
      toast.success('Paiement créé avec succès !');
      navigate('/admin/payments');
    } catch (err) {
      console.error('Failed to create payment:', err);
      setError('Échec de création du paiement.');
      toast.error('Erreur lors de la création du paiement.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <p className="text-lg text-gray-600">Chargement des données...</p>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <p className="text-lg text-error-600">Erreur: {error}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4 flex items-center text-primary-600">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Retour aux Paiements
            </Button>
            <h1 className="text-3xl font-bold text-gray-900">Ajouter un Paiement (lié à une facture)</h1>
            <p className="text-gray-600 mt-2">Enregistrez un nouvel encaissement sur une facture.</p>
          </div>
          <Button onClick={handleSubmit} disabled={isSaving} className="flex items-center">
            <Save className="w-5 h-5 mr-2" />
            {isSaving ? 'Création...' : 'Créer le Paiement'}
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="invoiceId" className="block text-sm font-medium text-gray-700">Facture liée</label>
              <select
                name="invoiceId"
                id="invoiceId"
                value={formData.invoiceId}
                onChange={handleInvoiceChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              >
                <option value="">Sélectionner une facture</option>
                {(() => { console.log('Invoices for rendering options:', invoices); return null; })()}
                {Array.isArray(invoices) && invoices.map((inv) => {
                  const cli = inv.clientId || inv.client;
                  const name = cli ? `${cli.firstName ?? ''} ${cli.lastName ?? ''}`.trim() : '';
                  return (
                    <option key={inv._id} value={inv._id}>
                      {inv.invoiceNumber ?? inv._id} {name ? `— ${name}` : ''}
                    </option>
                  );
                })}
              </select>
            </div>

            <div>
              <label htmlFor="paymentNumber" className="block text-sm font-medium text-gray-700">N° Paiement</label>
              <input
                type="text"
                name="paymentNumber"
                id="paymentNumber"
                value={formData.paymentNumber}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Laisser vide pour auto"
              />
            </div>

            <div>
              <label htmlFor="amount" className="block text-sm font-medium text-gray-700">Montant</label>
              <input
                type="number"
                name="amount"
                id="amount"
                step="0.01"
                value={formData.amount}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>

            <div>
              <label htmlFor="currency" className="block text-sm font-medium text-gray-700">Devise</label>
              <input
                type="text"
                name="currency"
                id="currency"
                value={formData.currency}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>

            <div>
              <label htmlFor="paymentMethod" className="block text-sm font-medium text-gray-700">Méthode</label>
              <input
                type="text"
                name="paymentMethod"
                id="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>

            <div>
              <label htmlFor="paymentDate" className="block text-sm font-medium text-gray-700">Date de Paiement</label>
              <input
                type="date"
                name="paymentDate"
                id="paymentDate"
                value={formData.paymentDate}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>

            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700">Statut</label>
              <select
                name="status"
                id="status"
                value={formData.status}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              >
                <option value="completed">Terminé</option>
                <option value="pending">En attente</option>
                <option value="failed">Échoué</option>
                <option value="refunded">Remboursé</option>
              </select>
            </div>

          </div>

          <div className="space-y-4">
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700">Notes Additionnelles</label>
              <textarea
                name="notes"
                id="notes"
                rows={3}
                value={formData.notes}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {error && <p className="text-error-600 text-sm mt-4">{error}</p>}

          <div className="flex justify-end space-x-3">
            <Button type="button" variant="secondary" onClick={() => navigate(-1)}>
              Annuler
            </Button>
            <Button type="submit" disabled={isSaving}>
              <Save className="w-5 h-5 mr-2" />
              {isSaving ? 'Création...' : 'Créer le Paiement'}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default AddPayment;
